import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AddCompanyLayout from "./AddCompanyLayout";
import {
  COMPANY_STEPS,
  CompanyFormData,
  INITIAL_FORM_DATA,
} from "../types/company-types";
import Identity from "./steps/Identity";
import Branches from "./steps/Branches";
import Representatives from "./steps/Representatives";
import Branding from "./steps/Branding";
import apiService from "../../../service/Api";
import { normalizeUrl } from "../../../utils/normalizeUrl";

export default function AddCompanyV2() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<CompanyFormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Validation per step ──────────────────────────────────────────────────
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0:
        if (!formData.name.trim()) {
          toast.error("Company name is required.");
          return false;
        }
        if (!formData.phone.trim()) {
          toast.error("Phone number is required.");
          return false;
        }
        if (!formData.primaryEmail.trim()) {
          toast.error("Email is required.");
          return false;
        }
        return true;

      case 1: {
        const unnamedBranch = formData.branches.find((b) => !b.name.trim());
        if (unnamedBranch) {
          toast.error("All branches must have a name.");
          return false;
        }
        return true;
      }

      case 2: {
        const unnamedRep = formData.representatives.find(
          (r) => !r.firstName.trim(),
        );
        if (unnamedRep) {
          toast.error("All representatives must have a first name.");
          return false;
        }
        return true;
      }

      case 3:
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) return;
    setActiveStep((prev) => Math.min(prev + 1, COMPANY_STEPS.length - 1));
  };

  const handlePrevious = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };
  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    for (let i = 0; i < COMPANY_STEPS.length; i++) {
      if (!validateStep(i)) {
        setActiveStep(i);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Split reps into new and existing
      const newReps = formData.representatives.filter(
        (r) => !r.existingContactId,
      );
      const existingReps = formData.representatives.filter(
        (r) => r.existingContactId,
      );

      const payload: Record<string, unknown> = {
        name: formData.name,
        phone_number: formData.phone,
        primary_email: formData.primaryEmail,
        street_address: formData.address,
        city: formData.city,
        company_type: formData.industry,
        country: "Zimbabwe",
        time_zone: "Africa/Harare",
        company_tags: formData.tags,
        website: normalizeUrl(formData.website),
        linkedin_profile: normalizeUrl(formData.linkedin),
        youtube_profile: normalizeUrl(formData.youtube),
        facebook_profile: normalizeUrl(formData.facebook),
        instagram_profile: normalizeUrl(formData.instagram),
        x_profile: normalizeUrl(formData.x),
        // Only new contacts go in payload
        contacts: newReps.map((r) => ({
          first_name: r.firstName,
          last_name: r.lastName,
          role: r.role,
          phone: r.phone,
          email: r.email,
        })),

        company_branches: JSON.stringify(
          formData.branches.map((b) => ({
            name: b.name,

            phone_number: b.phone,
            email: b.email,
            street_address: b.address,
            city: b.city,
            country: "Zimbabwe",
          })),
        ),
      };

      const res = await apiService.post<{
        id: number;
        hq_branch: { id: number };
      }>("api/companies/company/full-create/", payload);

      // PATCH existing contacts to link them to HQ branch
      if (existingReps.length > 0 && res.data.hq_branch?.id) {
        await Promise.all(
          existingReps.map((r) =>
            apiService.patch(`api/contacts/contact/${r.existingContactId}/`, {
              company_branch_id: res.data.hq_branch.id,
            }),
          ),
        );
      }

      // Upload logo separately if provided
      // Upload logo separately if provided
      if (formData.logoMode === "upload" && formData.logoFile && res.data.id) {
        const logoForm = new FormData();
        logoForm.append("company_logo", formData.logoFile);
        await apiService.patch(
          `api/companies/company/${res.data.id}/`,
          logoForm,
        );
      } else if (
        formData.logoMode === "url" &&
        formData.logoUrl.trim() &&
        res.data.id
      ) {
        try {
          const imgRes = await fetch(normalizeUrl(formData.logoUrl));
          if (!imgRes.ok) throw new Error(`Fetch failed: ${imgRes.status}`);
          const blob = await imgRes.blob();

          if (!blob.type.startsWith("image/")) {
            throw new Error("URL did not return an image");
          }

          const ext = blob.type.split("/")[1]?.split("+")[0] || "jpg";
          const logoForm = new FormData();
          logoForm.append("company_logo", blob, `logo-from-url.${ext}`);
          await apiService.patch(
            `api/companies/company/${res.data.id}/`,
            logoForm,
          );
        } catch (err) {
          console.error("Logo URL fetch failed:", err);
          toast.error(
            "Company saved, but the logo URL couldn't be fetched. You can upload it manually from the company page.",
          );
        }
      }

      toast.success("Company created successfully!");
      navigate(`/companies/${res.data.id}`);
    } catch (err) {
      console.error("Error creating company:", err);
      toast.error("Failed to create company. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step content map ─────────────────────────────────────────────────────
  const stepContent: Record<number, React.ReactNode> = {
    0: <Identity formData={formData} setFormData={setFormData} />,
    1: <Branches formData={formData} setFormData={setFormData} />,
    2: <Representatives formData={formData} setFormData={setFormData} />,
    3: <Branding formData={formData} setFormData={setFormData} />,
  };

  return (
    <AddCompanyLayout
      activeStep={activeStep}
      formData={formData}
      setFormData={setFormData}
      onStepClick={handleStepClick}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    >
      {stepContent[activeStep]}
    </AddCompanyLayout>
  );
}
